import type { WatchQueryFetchPolicy } from '@apollo/client/core/watchQueryOptions';
import type { QueryResult } from '@apollo/client/react';
import type { FeaturedWidgetInstance } from '@aurora/shared-client/components/context/FeaturedWidgetContext/FeaturedWidgetContext';
import FeaturedWidgetContext from '@aurora/shared-client/components/context/FeaturedWidgetContext/FeaturedWidgetContext';
import TenantContext from '@aurora/shared-client/components/context/TenantContext';
import ideaStatusGroupsQuery from '@aurora/shared-client/components/ideas/IdeaStatusGroups.query.graphql';
import useQueryWithTracing from '@aurora/shared-client/components/useQueryWithTracing';
import { extractStatusesFromGroupTree } from '@aurora/shared-client/helpers/ideas/IdeasHelper';
import type {
  Idea,
  IdeaStatusDataEdge,
  IdeaStatusDetails
} from '@aurora/shared-generated/types/graphql-schema-types';
import type {
  IdeaStatusGroupsQuery,
  IdeaStatusGroupsQueryVariables
} from '@aurora/shared-generated/types/graphql-types';
import { useContext } from 'react';
import type {
  FeaturedIdeaStatusesWidgetQuery,
  FeaturedIdeaStatusesWidgetQueryVariables
} from '../../../../types/graphql-types';
import featuredIdeaStatusesWidgetQuery from './../FeaturedIdeaStatusesWidget.query.graphql';

/**
 * Take in the list of selected featured status keys together with the idea status json data
 * and return a list of the selected statuses with their properties
 * @param ideaStatusGroupsQueryData the idea status json for the current board
 * @param featuredItemIds list of selected featured status keys
 * @returns Array of featured statuses with all their properties
 */
function getProvisionedFeaturedIdeaStatuses(
  ideaStatusGroupsQueryData: IdeaStatusGroupsQuery,
  featuredItemIds: string[]
): IdeaStatusDataEdge[] {
  const ideaBoard = ideaStatusGroupsQueryData?.coreNode as Idea;
  if (ideaBoard != null) {
    const ideaStatusList: IdeaStatusDetails[] = extractStatusesFromGroupTree(
      ideaBoard.ideaStatusGroups.groups,
      featuredItemIds,
      'includes'
    );
    return ideaStatusList.map(ideaStatus => {
      return {
        node: ideaStatus
      };
    });
  }
}

/**
 * Helper for using the FeaturedIdeaStatusesWidget query
 *
 * @param queryVariables variables used by the FeaturedIdeaStatusesWidget query
 * @param skip whether to skip the featured content widget query
 * @param fetchPolicy the fetch policy for the query
 * @param onCompleted callback function invoked when
 */
export default function useFeaturedIdeaStatusesWidget(
  queryVariables: FeaturedIdeaStatusesWidgetQueryVariables,
  skip = false,
  fetchPolicy: WatchQueryFetchPolicy = 'cache-first',
  onCompleted?: (data: FeaturedIdeaStatusesWidgetQuery) => void
): QueryResult<FeaturedIdeaStatusesWidgetQuery, FeaturedIdeaStatusesWidgetQueryVariables> {
  const {
    publicConfig: { auroraFeaturedWidgetsEnabled, messageStatusEnabled }
  } = useContext(TenantContext);
  const featuredIdeaStatusesWidgetEnabled = auroraFeaturedWidgetsEnabled && messageStatusEnabled;
  const { featuredWidgetInstances } = useContext(FeaturedWidgetContext);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { instanceId, quiltId, ideaBoardId, ...restQueryVariables } = queryVariables;

  const sessionInstance: FeaturedWidgetInstance = featuredWidgetInstances?.find(
    featuredWidgetInstance => featuredWidgetInstance.instanceId === instanceId
  );
  const sessionInstanceHasIdeaStatuses: boolean = sessionInstance?.featuredItemIds?.length > 0;
  const skipQuery: boolean = skip || !featuredIdeaStatusesWidgetEnabled || !!sessionInstance;
  const skipProvisionalQuery: boolean =
    skip ||
    !featuredIdeaStatusesWidgetEnabled ||
    !sessionInstance ||
    // API throws an error when using the ideaStatus id constraint and providing an empty array of ids, so skip
    (sessionInstance && !sessionInstanceHasIdeaStatuses);

  // retrieves the ideaStatuses and last modified information if the widget is already stored
  const featuredIdeaStatusesWidgetQueryResult = useQueryWithTracing<
    FeaturedIdeaStatusesWidgetQuery,
    FeaturedIdeaStatusesWidgetQueryVariables
  >(module, featuredIdeaStatusesWidgetQuery, {
    variables: queryVariables,
    skip: skipQuery,
    fetchPolicy,
    onCompleted
  });

  const ideaGroupsQueryResult = useQueryWithTracing<
    IdeaStatusGroupsQuery,
    IdeaStatusGroupsQueryVariables
  >(module, ideaStatusGroupsQuery, {
    variables: {
      id: ideaBoardId,
      useGroupKey: true,
      useDescription: false,
      useLabel: true
    },
    skip: skipProvisionalQuery,
    fetchPolicy,
    onCompleted: data => {
      const result: FeaturedIdeaStatusesWidgetQuery = {
        featuredStatusesWidget: {
          statuses: {
            totalCount: sessionInstance?.featuredItemIds.length,
            edges: getProvisionedFeaturedIdeaStatuses(data, sessionInstance?.featuredItemIds)
          }
        }
      };
      if (onCompleted) {
        onCompleted(result);
      }
    }
  });

  if (!sessionInstance) {
    return featuredIdeaStatusesWidgetQueryResult;
  } else {
    const { loading, error, data } = ideaGroupsQueryResult;

    // fallbacks provided in the event the widget was added during the current session but the query is skipped due
    //  to no ideaStatuses yet being associated
    return {
      ...featuredIdeaStatusesWidgetQueryResult,
      loading: loading || false,
      error: error || null,
      data: {
        featuredStatusesWidget: {
          statuses: {
            totalCount: sessionInstance?.featuredItemIds.length,
            edges: getProvisionedFeaturedIdeaStatuses(data, sessionInstance?.featuredItemIds)
          }
        }
      }
    };
  }
}
